import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, uploadUrl } from '../hooks/useApi';
import ModalLierTransaction from './ModalLierTransaction';
import AuditTrailBatch from './AuditTrailBatch';
import FormCorrectionAppend from './FormCorrectionAppend';
import Icon from './Icon';
import './EtapeTracabilite.css';

const LABEL_CAT = {
  viande:          'Viandes',
  produit_laitier: 'Produits laitiers',
  frais_legume:    'Légumes frais',
  frais_autre:     'Autres frais',
};

const ICON_CAT = {
  viande:          'viande',
  produit_laitier: 'laitier',
  frais_legume:    'legume',
  frais_autre:     'package',
};

const PRESETS_SECTIONS = [
  { nom: 'Viande',      icon: 'viande',    cat: 'viande' },
  { nom: 'Emballage',   icon: 'emballage', cat: 'frais_autre' },
  { nom: 'Épices',      icon: 'epices',    cat: 'frais_autre' },
  { nom: 'Bois fumage', icon: 'bois',      cat: 'frais_autre' },
  { nom: 'Transport',   icon: 'transport', cat: 'frais_autre' },
  { nom: 'Équipement',  icon: 'package',   cat: 'frais_autre' },
];

const TYPES_SECTION_CUSTOM = [
  { value: 'viande',    label: 'Viande' },
  { value: 'emballage', label: 'Emballage' },
  { value: 'epices',    label: 'Ingrédient' },
  { value: 'bois',      label: 'Bois / Fumage' },
  { value: 'transport', label: 'Transport' },
  { value: 'package',   label: 'Autre' },
];

function formatDate(d) {
  if (!d) return '—';
  return d.slice(0, 10);
}
function formatMontant(m) {
  if (m == null) return '—';
  return Math.abs(Number(m)).toFixed(2) + ' $';
}

// ─── Card ingrédient enrichie ─────────────────────────────────────────────────

function CardIngredientTracabilite({
  ingredient,
  liaison,
  localData,
  saving,
  onUpdate,
  onSauvegarder,
  onDetacherFacture,
  onLierFacture,
}) {
  const est_lie = !!liaison;
  const a_facture = !!(liaison?.journal_id);

  return (
    <div className="ingredient-tracabilite-card">
      <div className="ingredient-tracabilite-header">
        <span className="ingredient-nom">{ingredient.nom}</span>
        {est_lie ? (
          <span className="badge badge--success">
            <Icon name="check" size={11} weight="bold" /> Lié
          </span>
        ) : (
          <span className="badge badge--warning">
            <Icon name="attention" size={11} /> Incomplet
          </span>
        )}
      </div>

      <div className="ingredient-tracabilite-grid">
        <div className="form-groupe">
          <label>Fournisseur</label>
          <input
            type="text"
            placeholder="ex: Costco, IGA…"
            value={localData.fournisseur_specifique || ''}
            onChange={e => onUpdate('fournisseur_specifique', e.target.value)}
          />
        </div>
        <div className="form-groupe">
          <label>Numéro de lot</label>
          <input
            type="text"
            placeholder="ex: LOT-2026-0342"
            value={localData.lot_specifique || ''}
            onChange={e => onUpdate('lot_specifique', e.target.value)}
          />
        </div>
        <div className="form-groupe">
          <label>Date d'achat MP</label>
          <input
            type="date"
            value={localData.date_achat_specifique || ''}
            onChange={e => onUpdate('date_achat_specifique', e.target.value)}
          />
        </div>
        <div className="form-groupe">
          <label>Date d'expiration MP</label>
          <input
            type="date"
            value={localData.date_expiration_specifique || ''}
            onChange={e => onUpdate('date_expiration_specifique', e.target.value)}
          />
        </div>
      </div>

      <div className="ingredient-facture">
        <label>Facture d'achat</label>
        {a_facture ? (
          <div className="facture-liee">
            <span className="facture-info">
              <Icon name="check" size={13} weight="bold" />
              {liaison.fournisseur ? ` ${liaison.fournisseur} —` : ''} {formatDate(liaison.date_trans)} — {formatMontant(liaison.montant)}
            </span>
            <div className="facture-actions">
              {liaison.recu_fichier && (
                <a
                  href={uploadUrl(`/uploads/recus/${liaison.recu_fichier}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn--sm"
                >
                  <Icon name="document" size={13} /> Voir
                </a>
              )}
              <button
                className="btn btn-ghost btn--sm"
                style={{ color: 'var(--danger)' }}
                onClick={onDetacherFacture}
              >
                <Icon name="fermer" size={13} /> Retirer
              </button>
            </div>
          </div>
        ) : (
          <button className="btn-lier-facture" onClick={onLierFacture}>
            <Icon name="lien" size={14} /> Lier une facture (optionnel)
          </button>
        )}
      </div>

      <div className="ingredient-save">
        <button
          className="btn-save-ingredient"
          onClick={onSauvegarder}
          disabled={saving}
        >
          <Icon name="sauvegarder" size={13} />
          {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  );
}

// ─── Carte de liaison (sections manuelles) ───────────────────────────────────

function CarteLiaison({ liaison, onRetirer }) {
  return (
    <div className="trac-liaison-card trac-liaison-card--liee">
      <div className="trac-liaison-card__body">
        <div className="trac-liaison-card__titre">
          <Icon name="check" size={13} weight="bold" color="var(--success)" />
          {liaison.fournisseur && <strong>{liaison.fournisseur}</strong>}
          {formatDate(liaison.date_trans)} — {formatMontant(liaison.montant)}
        </div>
        <div className="trac-liaison-card__desc">{liaison.description}</div>
        {liaison.poids_utilise_kg != null && (
          <div className="muted" style={{ fontSize: 12 }}>
            {liaison.poids_utilise_kg} utilisés dans ce batch
          </div>
        )}
      </div>
      <div className="trac-liaison-card__actions">
        {liaison.recu_fichier && (
          <a
            href={uploadUrl(`/uploads/recus/${liaison.recu_fichier}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn--sm"
          >
            <Icon name="document" size={13} /> Facture
          </a>
        )}
        <button
          className="btn btn-ghost btn--sm"
          style={{ color: 'var(--danger)' }}
          onClick={onRetirer}
        >
          <Icon name="fermer" size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Section manuelle ─────────────────────────────────────────────────────────

function SectionManuelle({ section, liaisonsSection, batchId, onLiee, onRetirer, onSupprimer }) {
  const [modal, setModal] = useState(false);

  const ingredientVirtuel = {
    id: null,
    nom: section.nom,
    unite: '',
    section_label: section.nom,
    section_emoji: section.icon || '',
  };

  return (
    <div className="trac-section-manuelle">
      <div className="trac-section-manuelle__header">
        <span className="trac-section-manuelle__titre">
          {section.icon && <Icon name={section.icon} size={15} />}
          {section.nom}
        </span>
        <button
          className="btn btn-ghost btn--sm"
          style={{ color: 'var(--danger)' }}
          onClick={onSupprimer}
        >
          <Icon name="fermer" size={13} />
        </button>
      </div>

      {liaisonsSection.length === 0 ? (
        <div className="trac-ingredient__vide">
          <Icon name="attention" size={14} color="var(--warning)" />
          <span className="muted" style={{ fontSize: 13 }}>Aucune facture liée</span>
        </div>
      ) : (
        <div className="trac-ingredient__liaisons">
          {liaisonsSection.map(l => (
            <CarteLiaison
              key={l.id}
              liaison={l}
              onRetirer={() => onRetirer(l.id)}
            />
          ))}
        </div>
      )}

      <button
        className="btn btn-ghost btn--sm"
        style={{ marginTop: 6 }}
        onClick={() => setModal(true)}
      >
        <Icon name="lien" size={13} /> Lier une facture
      </button>

      {modal && (
        <ModalLierTransaction
          batchId={batchId}
          ingredient={ingredientVirtuel}
          onClose={() => setModal(false)}
          onLiee={() => { setModal(false); onLiee(); }}
        />
      )}
    </div>
  );
}

// ─── Modal ajout section manuelle ─────────────────────────────────────────────

function ModalAddSection({ onAjouter, onClose }) {
  const [nomCustom, setNomCustom]   = useState('');
  const [iconCustom, setIconCustom] = useState('package');

  return (
    <div className="lier-overlay" onClick={onClose}>
      <div className="lier-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="lier-modal__header">
          <h3>Ajouter une section de traçabilité</h3>
          <button className="lier-modal__close" onClick={onClose}>
            <Icon name="fermer" size={16} />
          </button>
        </div>

        <div className="lier-modal__body">
          <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>Sections rapides :</p>
          <div className="trac-presets-grid">
            {PRESETS_SECTIONS.map(preset => (
              <button
                key={preset.nom}
                className="trac-preset-btn"
                onClick={() => onAjouter(preset)}
              >
                <Icon name={preset.icon} size={14} /> {preset.nom}
              </button>
            ))}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' }} />

          <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>Ou créer une section personnalisée :</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <select
              value={iconCustom}
              onChange={e => setIconCustom(e.target.value)}
              style={{ width: 140, flexShrink: 0 }}
            >
              {TYPES_SECTION_CUSTOM.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <input
              style={{ flex: 1 }}
              placeholder="Nom de la section…"
              value={nomCustom}
              onChange={e => setNomCustom(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && nomCustom.trim()) {
                  onAjouter({ nom: nomCustom.trim(), icon: iconCustom, cat: 'frais_autre' });
                }
              }}
              autoFocus
            />
          </div>
        </div>

        <div className="lier-modal__footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            disabled={!nomCustom.trim()}
            onClick={() => onAjouter({ nom: nomCustom.trim(), icon: iconCustom, cat: 'frais_autre' })}
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EtapeTracabilite ─────────────────────────────────────────────────────────

export default function EtapeTracabilite({ batchId, batchNom, batchStatut, onNext, onBack, onNbNonLiesChange }) {
  const isFinalise = batchStatut === 'finalise';
  const [ingredientsFrais, setIngredientsFrais] = useState([]);
  const [liaisons, setLiaisons]                 = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [sectionsManuelle, setSectionsManuelle] = useState([]);
  const [modalAddSection, setModalAddSection]   = useState(false);
  const [liaisonsLocal, setLiaisonsLocal]       = useState({});
  const [savingMap, setSavingMap]               = useState({});
  const [toast, setToast]                       = useState('');
  // { ingredient, liaisonId } pour le modal lier facture sur un ingrédient frais
  const [modalLier, setModalLier]               = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  // Ref pour savoir quelles cartes ont des éditions en cours non sauvegardées
  const cartesEnEditionRef = useRef(new Set());

  const charger = useCallback(() => {
    if (!batchId) return;
    setLoading(true);
    api.get(`/batches/${batchId}/ingredients-frais`)
      .then(data => {
        const ings  = data.ingredients_frais    || [];
        const liens = data.liaisons_transactions || [];
        setIngredientsFrais(ings);
        setLiaisons(liens);

        // Bug B fix : merger les données DB avec l'état local des cartes en cours d'édition
        // au lieu d'écraser aveuglément tout l'état local.
        const cartesEnEdition = cartesEnEditionRef.current;
        setLiaisonsLocal(prevLocal => {
          const newLocal = {};
          liens.filter(l => l.matiere_id).forEach(l => {
            // Si cette carte est en cours d'édition par l'utilisateur, on préserve son état local
            if (cartesEnEdition.has(l.matiere_id)) {
              newLocal[l.matiere_id] = prevLocal[l.matiere_id] || {
                fournisseur_specifique:     l.fournisseur_specifique     || '',
                lot_specifique:             l.lot_specifique             || '',
                date_achat_specifique:      l.date_achat_specifique      || '',
                date_expiration_specifique: l.date_expiration_specifique || '',
              };
            } else {
              // Carte sauvegardée ou non touchée : synchroniser depuis la DB
              newLocal[l.matiere_id] = {
                fournisseur_specifique:     l.fournisseur_specifique     || '',
                lot_specifique:             l.lot_specifique             || '',
                date_achat_specifique:      l.date_achat_specifique      || '',
                date_expiration_specifique: l.date_expiration_specifique || '',
              };
            }
          });
          // Préserver aussi les cartes en édition qui n'ont pas encore de liaison DB
          cartesEnEdition.forEach(id => {
            if (!newLocal[id] && prevLocal[id]) {
              newLocal[id] = prevLocal[id];
            }
          });
          return newLocal;
        });

        // Reconstruire les sections manuelles depuis les liaisons existantes
        const sectionsVuDb = [];
        const labelsVus = new Set();
        liens.filter(l => l.section_label).forEach(l => {
          if (!labelsVus.has(l.section_label)) {
            labelsVus.add(l.section_label);
            sectionsVuDb.push({
              id: `manual_db_${l.section_label}`,
              nom: l.section_label,
              icon: 'package', // section_emoji de la DB est un emoji legacy — on utilise l'icône générique
              est_manuel: true,
            });
          }
        });
        setSectionsManuelle(prev => {
          const labelsLocaux = new Set(prev.map(s => s.nom));
          const nouvelles = sectionsVuDb.filter(s => !labelsLocaux.has(s.nom));
          return [...prev, ...nouvelles];
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batchId]);

  useEffect(() => { charger(); }, [charger]);

  // ─── Liaisons par matière (première liaison par ingrédient)
  const liaisonsParMatiere = {};
  liaisons.filter(l => l.matiere_id).forEach(l => {
    if (!liaisonsParMatiere[l.matiere_id]) liaisonsParMatiere[l.matiere_id] = l;
  });

  // ─── Liaisons par section manuelle
  const liaisonsParSection = {};
  liaisons.filter(l => l.section_label).forEach(l => {
    if (!liaisonsParSection[l.section_label]) liaisonsParSection[l.section_label] = [];
    liaisonsParSection[l.section_label].push(l);
  });

  // ─── Actions ingrédients frais
  const updateLiaison = (matiere_id, champ, valeur) => {
    // Bug B fix : marquer cette carte comme "en cours d'édition" pour que charger() ne l'écrase pas
    cartesEnEditionRef.current = new Set([...cartesEnEditionRef.current, matiere_id]);
    setLiaisonsLocal(prev => ({
      ...prev,
      [matiere_id]: { ...(prev[matiere_id] || {}), [champ]: valeur },
    }));
  };

  const sauvegarderIngredient = async (matiere_id) => {
    const local   = liaisonsLocal[matiere_id] || {};
    const liaison = liaisonsParMatiere[matiere_id];
    setSavingMap(prev => ({ ...prev, [matiere_id]: true }));
    try {
      if (liaison) {
        await api.put(`/batches/liaisons/${liaison.id}`, local);
      } else {
        await api.post(`/batches/${batchId}/tracabilite-ingredient`, { matiere_id, ...local });
      }
      // Dé-marquer cette carte : elle vient d'être sauvegardée, charger() peut la ré-init
      cartesEnEditionRef.current = new Set(
        [...cartesEnEditionRef.current].filter(id => id !== matiere_id)
      );
      showToast('Sauvegardé ✓');
      charger();
    } catch {
      showToast('Erreur lors de la sauvegarde');
    } finally {
      setSavingMap(prev => ({ ...prev, [matiere_id]: false }));
    }
  };

  // Bug A fix : sauvegarder toutes les cartes modifiées avant de naviguer vers l'étape suivante
  const handleNext = async () => {
    const cartesEnEdition = [...cartesEnEditionRef.current];
    if (cartesEnEdition.length === 0) {
      onNext();
      return;
    }
    // Sauvegarder toutes les cartes avec des modifications non persistées
    const promises = cartesEnEdition.map(matiere_id => sauvegarderIngredient(matiere_id));
    try {
      await Promise.all(promises);
    } catch {
      // Même en cas d'erreur partielle, on navigue (les erreurs individuelles sont déjà affichées)
    }
    onNext();
  };

  const detacherFacture = async (matiere_id) => {
    const liaison = liaisonsParMatiere[matiere_id];
    if (!liaison) return;
    try {
      await api.put(`/batches/liaisons/${liaison.id}`, { journal_id: null });
      charger();
    } catch {}
  };

  const retirerLiaisonSection = async (liaisonId) => {
    try {
      await api.delete(`/batches/${batchId}/transactions-liees/${liaisonId}`);
      charger();
    } catch {}
  };

  // ─── Comptage "sans facture" = sans journal_id
  const nbIngAvecFacture = ingredientsFrais.filter(i => liaisonsParMatiere[i.id]?.journal_id).length;
  const nbManLies        = sectionsManuelle.filter(s => (liaisonsParSection[s.nom] || []).some(l => l.journal_id)).length;
  const nb_total         = ingredientsFrais.length + sectionsManuelle.length;
  const nb_lies          = nbIngAvecFacture + nbManLies;
  const nb_non_lies      = nb_total - nb_lies;
  const tout_lie         = nb_total > 0 && nb_non_lies === 0;

  useEffect(() => {
    if (onNbNonLiesChange) onNbNonLiesChange(nb_non_lies);
  }, [nb_non_lies, onNbNonLiesChange]);

  // ─── Sections manuelles
  const ajouterSectionManuelle = (preset) => {
    const deja = sectionsManuelle.find(s => s.nom === preset.nom);
    if (deja) { setModalAddSection(false); return; }
    setSectionsManuelle(prev => [...prev, {
      id: `manual_${Date.now()}`,
      nom: preset.nom,
      icon: preset.icon || 'package',
      categorie_tracabilite: preset.cat,
      est_manuel: true,
    }]);
    setModalAddSection(false);
  };

  const supprimerSectionManuelle = (id) => {
    setSectionsManuelle(prev => prev.filter(s => s.id !== id));
  };

  // ─── Grouper ingrédients par catégorie
  const categories = ['viande', 'produit_laitier', 'frais_legume', 'frais_autre'];
  const parCat = {};
  categories.forEach(cat => {
    parCat[cat] = ingredientsFrais.filter(i => i.categorie_tracabilite === cat);
  });

  if (!batchId) return null;

  return (
    <div className="etape-content">
      {toast && <div className="trac-toast">{toast}</div>}

      <div className="card form-section trac-etape">
        <div className="trac-etape__header">
          <div>
            <h3>
              <Icon name="lien" size={16} />
              Traçabilité du batch
            </h3>
            {batchNom && <p className="muted" style={{ fontSize: 13, marginTop: 2 }}>{batchNom}</p>}
          </div>
          <button className="btn btn-ghost btn--sm" onClick={() => setModalAddSection(true)}>
            <Icon name="ajouter" size={13} /> Ajouter section
          </button>
        </div>

        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          Associez vos factures d'achat à ce batch. Cette étape est optionnelle mais recommandée
          pour une traçabilité complète (MAPAQ).
        </p>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="skeleton" style={{ height: 80, borderRadius: 'var(--radius-lg)' }} />
            <div className="skeleton" style={{ height: 80, borderRadius: 'var(--radius-lg)' }} />
          </div>
        ) : (
          <>
            {/* ── Ingrédients frais auto-détectés ── */}
            {ingredientsFrais.length > 0 && (
              <>
                <div className="trac-section-titre">Ingrédients frais</div>
                {categories.map(cat => {
                  const items = parCat[cat] || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={cat} className="trac-categorie">
                      <div className="trac-categorie__label">
                        <Icon name={ICON_CAT[cat]} size={12} />
                        {LABEL_CAT[cat]}
                      </div>
                      {items.map(ing => (
                        <CardIngredientTracabilite
                          key={ing.id}
                          ingredient={ing}
                          liaison={liaisonsParMatiere[ing.id] || null}
                          localData={liaisonsLocal[ing.id] || {}}
                          saving={savingMap[ing.id] || false}
                          onUpdate={(champ, val) => updateLiaison(ing.id, champ, val)}
                          onSauvegarder={() => sauvegarderIngredient(ing.id)}
                          onDetacherFacture={() => detacherFacture(ing.id)}
                          onLierFacture={() => setModalLier({
                            ingredient: ing,
                            liaisonId: liaisonsParMatiere[ing.id]?.id || null,
                          })}
                        />
                      ))}
                    </div>
                  );
                })}
              </>
            )}

            {/* ── Sections manuelles ── */}
            {sectionsManuelle.length > 0 && (
              <>
                <div className="trac-section-titre">Sections manuelles</div>
                {sectionsManuelle.map(sec => (
                  <SectionManuelle
                    key={sec.id}
                    section={sec}
                    liaisonsSection={liaisonsParSection[sec.nom] || []}
                    batchId={batchId}
                    onLiee={charger}
                    onRetirer={retirerLiaisonSection}
                    onSupprimer={() => supprimerSectionManuelle(sec.id)}
                  />
                ))}
              </>
            )}

            {ingredientsFrais.length === 0 && sectionsManuelle.length === 0 && (
              <div className="empty-state">
                <Icon name="liste" size={32} />
                <span className="empty-state__titre">Aucun ingrédient frais détecté</span>
                <span>Vous pouvez ajouter des sections manuellement.</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Section Historique & Corrections (batch finalisé) ── */}
      {isFinalise && batchId && (
        <div style={{ marginTop: 'var(--space-5)' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-3)',
            paddingBottom: 'var(--space-3)',
            borderBottom: '1px solid var(--border)',
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', fontFamily: 'Cinzel, serif', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Historique &amp; Corrections
            </span>
            <span className="badge badge--gold">Batch finalisé</span>
          </div>
          <AuditTrailBatch batchId={batchId} />
          <FormCorrectionAppend batchId={batchId} onSuccess={() => {}} />
        </div>
      )}

      {/* ── Actions ── */}
      <div className="trac-continuer">
        {nb_non_lies > 0 && (
          <div className="trac-avertissement">
            <Icon name="attention" size={15} />
            {nb_non_lies} ingrédient{nb_non_lies > 1 ? 's' : ''} sans facture liée.
            Vous pourrez compléter depuis le détail du batch.
          </div>
        )}

        <div className="etape-actions">
          <button className="btn btn-ghost" onClick={onBack}>
            <Icon name="retour" size={15} /> Retour
          </button>
          {tout_lie ? (
            <button className="btn btn-primary" onClick={handleNext}>
              <Icon name="succes" size={15} /> Tout est lié — Continuer
            </button>
          ) : (
            <button className="btn btn-ghost" onClick={handleNext}>
              Continuer sans tout lier
              <Icon name="chevron" size={15} />
            </button>
          )}
        </div>
      </div>

      {modalAddSection && (
        <ModalAddSection
          onAjouter={ajouterSectionManuelle}
          onClose={() => setModalAddSection(false)}
        />
      )}

      {modalLier && (
        <ModalLierTransaction
          batchId={batchId}
          ingredient={modalLier.ingredient}
          liaisonIdExistant={modalLier.liaisonId}
          onClose={() => setModalLier(null)}
          onLiee={() => { setModalLier(null); charger(); }}
        />
      )}
    </div>
  );
}
